// Task domain types — per Schema_Specification Section 3.8 (2.9.x / 2.2.4).

import i18n from '@/i18n'
import type { ListQuery } from './index'
import type { User } from './auth'
import type { ActivityLog } from './clients'

export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent'
export type TaskStatus = 'todo' | 'in_progress' | 'done' | 'cancelled'

export interface Task {
  id: string
  tenant_id: string
  title: string
  description: string | null
  priority: TaskPriority
  status: TaskStatus
  due_date: string | null
  assigned_to: string | null
  related_type: string | null
  related_id: string | null
  // Present on list rows (feedback #68) so Call/WhatsApp can dial the assignee.
  assignee_name?: string | null
  assignee_phone?: string | null
  created_at: string
  updated_at: string
}

// To-do checklist item (feedback #69).
export interface TaskChecklistItem {
  id: string
  task_id: string
  label: string
  is_done: boolean
  position: number
  created_at: string
  updated_at: string
}

// Comment author summary (feedback #71).
export interface TaskCommentAuthor {
  id: string
  full_name: string | null
  avatar: string | null
}

// Comment (feedback #71). Add-only: no edit/delete/react.
export interface TaskComment {
  id: string
  task_id: string
  user_id: string | null
  body: string
  author: TaskCommentAuthor | null
  created_at: string
  updated_at: string
}

// Attached file (feedback #70).
export interface TaskFile {
  id: string
  name: string | null
  mime_type: string | null
  size: number | null
  url: string | null
  created_at: string
}

export interface TaskDetail extends Task {
  assignee: User | null
  checklist_items?: TaskChecklistItem[]
  comments?: TaskComment[]
  files?: TaskFile[]
  status_history?: ActivityLog[]
}

export interface CreateChecklistItemRequest {
  label: string
  is_done?: boolean
  position?: number
}

export interface UpdateChecklistItemRequest {
  label?: string
  is_done?: boolean
  position?: number
}

export interface CreateTaskCommentRequest {
  body: string
}

export interface CreateTaskRequest {
  title: string
  description?: string
  priority?: TaskPriority
  due_date?: string
  assigned_to?: string
  related_type?: string
  related_id?: string
}

export type EditTaskRequest = Partial<CreateTaskRequest>

export interface UpdateTaskStatusRequest {
  status: TaskStatus
}

export interface TaskListQuery extends ListQuery {
  status?: TaskStatus | TaskStatus[]
  priority?: TaskPriority
  assigned_to?: string
  due_date_from?: string
  due_date_to?: string
  overdue?: boolean | string
}

export interface TaskSummary {
  todo: number
  todo_due_today: number
  in_progress: number
  in_progress_assignee_count: number
  completed: number
  completed_this_week: number
  late: number
}

// Display labels (Test_Cases / AC text). The schema model maps New→todo and
// Assigned→todo-with-assignee; the UI surfaces the four operator-facing labels.
export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  todo: i18n.t('enums:taskStatus.todo'),
  in_progress: i18n.t('enums:taskStatus.in_progress'),
  done: i18n.t('enums:taskStatus.done'),
  cancelled: i18n.t('enums:taskStatus.cancelled'),
}

export const TASK_PRIORITY_LABELS: Record<TaskPriority, string> = {
  low: i18n.t('enums:taskPriority.low'),
  medium: i18n.t('enums:taskPriority.medium'),
  high: i18n.t('enums:taskPriority.high'),
  urgent: i18n.t('enums:taskPriority.urgent'),
}

// Linked-entity types (AC-2.9.2-016): Client / Quote / Invoice / Contract / Job.
export const TASK_RELATED_TYPES: {
  value: string
  label: string
  path: string
}[] = [
  {
    value: 'client',
    label: i18n.t('enums:taskRelatedType.client'),
    path: '/clients',
  },
  {
    value: 'quote',
    label: i18n.t('enums:taskRelatedType.quote'),
    path: '/quotes',
  },
  {
    value: 'invoice',
    label: i18n.t('enums:taskRelatedType.invoice'),
    path: '/invoices',
  },
  {
    value: 'contract',
    label: i18n.t('enums:taskRelatedType.contract'),
    path: '/contracts',
  },
  { value: 'job', label: i18n.t('enums:taskRelatedType.job'), path: '/jobs' },
]

// The backend stores `related_type` as the polymorphic morph type, which is the
// full model class name (e.g. "App\\Models\\Job"), while the options above key
// on short aliases ("job"). Normalize the stored value to the short alias so
// lookups/labels work regardless of which form the API returns.
export function normalizeRelatedType(related_type?: string | null): string {
  if (!related_type) return ''
  return (related_type.split('\\').pop() ?? '').toLowerCase()
}

// Resolve a stored `related_type` to its localized label (null if unknown).
export function relatedTypeLabel(related_type?: string | null): string | null {
  const key = normalizeRelatedType(related_type)
  return TASK_RELATED_TYPES.find(r => r.value === key)?.label ?? null
}

// Human-readable label for a linked entity in pickers / detail views.
export function taskRelatedEntityLabel(
  relatedType: string,
  item: {
    id: string
    number?: string | null
    name?: string | null
    reference?: string | null
    full_name?: string | null
  }
): string {
  switch (relatedType) {
    case 'quote':
    case 'invoice':
    case 'contract':
      return item.number ?? item.id
    case 'client':
      return item.name ?? item.full_name ?? item.id
    case 'job':
      return item.reference ?? item.id
    default:
      return (
        item.name ?? item.number ?? item.reference ?? item.full_name ?? item.id
      )
  }
}
